import {
    IEnum_Componentsharedcards_Backgroundcolour as CardBackgroundColourVariant,
    IComponentSharedLink,
    IMuxVideoUploaderMuxAssetEntityResponse,
    IUploadFileEntityResponse,
    Maybe,
} from '@/graphql/__generated__/global/types';
import { CardImageAspectRatio, determineAspectRatioByCardSpan } from '@/helpers/determineAspectRatioByCardSpan';
import classNames from '@/lib/classNames';
import { getImageUrl } from '@/lib/getImageUrl';
import { TRACKING_EVENT, track } from '@/lib/gtm';
import { VariantProps, cva } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import { theme } from 'tailwind.config';
import Button from '../Button';
import { LinkRenderer, findHref, isValidLink } from '../Link';
import RichText from '../RichText';
import { Card, CardContent, CardDescription, CardEyebrow, CardTitle } from './Card';

const LinkArrow = dynamic(() => import('@/icons/LinkArrow'));

const cardRendererVariants = cva('col-span-full', {
    variants: {
        span: {
            oneQuarter: 'sm:col-span-1 xl:col-span-2',
            oneThird: 'md:col-span-3 lg:col-span-2 xl:col-span-4',
            half: 'sm:col-span-3 xl:col-span-6',
            twoThirds: 'md:col-span-3 lg:col-span-4 xl:col-span-8',
        },
    },
});

interface CardRendererProps extends VariantProps<typeof cardRendererVariants> {
    card: {
        image?: IUploadFileEntityResponse | string;
        eyebrowText?: string;
        title?: string;
        description?: string;
        link?: Maybe<IComponentSharedLink>;
        id?: string;
        video?: IMuxVideoUploaderMuxAssetEntityResponse;
        hoverImage?: string;
        deferHoverImageLoad?: boolean;
        /**
         * Force the aspect ratio of the card image
         */
        aspectRatio?: number;
        sizes?: string;
        backgroundColour?: string;
        order?: string;
        tintImage?: Maybe<boolean>;
    };
}

export const CardRenderer = ({ card, span }: CardRendererProps) => {
    const { image, hoverImage, deferHoverImageLoad, eyebrowText, title, description, link, aspectRatio, backgroundColour, video, sizes, order, tintImage } = card;

    const hasImage = (typeof image === 'string' && Boolean(image)) || (typeof image === 'object' && Boolean(image?.data)) || (typeof video === 'object' && Boolean(video?.data));

    const imageRatio = aspectRatio ? aspectRatio : determineAspectRatioByCardSpan(span);

    const validLink = isValidLink(link?.linkToPage, link?.linkToStory, link?.linkToExternalPage);
    const href = validLink && findHref(link?.linkToPage, link?.linkToStory, link?.linkToExternalPage);

    /**
     * This sets up the inner content of the card
     *
     * If the card is just a text card with a link we render this as a button
     */
    const Content =
        eyebrowText || title || description ? (
            <CardContent spacing={eyebrowText && !hasImage ? 'between' : hasImage ? 'tight' : 'center'} className={classNames('relative', hasImage && 'absolute inset-0 z-10')}>
                {eyebrowText && <CardEyebrow className="mb-4">{eyebrowText}</CardEyebrow>}
                {/* This block ensures our spacing for the eyebrow to push to the top */}
                {backgroundColour === CardBackgroundColourVariant.Shell ? (
                    // If we have a shell background or no background colours we don't want to render the content & button in same div
                    <>
                        <div className="w-full">
                            <div className="flex items-end justify-between w-full gap-2">
                                <div className="">
                                    {title && <CardTitle>{title}</CardTitle>}
                                    <CardDescription className={classNames(hasImage && 'card-description')}>
                                        <RichText content={description as string} className={classNames(hasImage ? 'text-white lg:mx-0' : 'text-primary')} />
                                    </CardDescription>
                                </div>
                                {hasImage && link && <LinkArrow />}
                            </div>
                        </div>
                        {!hasImage && link && (
                            <LinkRenderer {...link}>
                                <Button
                                    className="mt-8"
                                    size="lg"
                                    onClick={() =>
                                        track(TRACKING_EVENT.PRESSED_BUTTON, {
                                            label: link?.label,
                                            url: href ? href : '',
                                        })
                                    }
                                >
                                    {link.label}
                                </Button>
                            </LinkRenderer>
                        )}
                    </>
                ) : (
                    // If we have a sand background we want to render the content & button in same div
                    <div className="w-full">
                        <div className="flex items-end justify-between w-full gap-2">
                            <div className="">
                                {title && <CardTitle>{title}</CardTitle>}
                                <CardDescription className={classNames(hasImage && 'card-description')}>
                                    <RichText content={description as string} className={classNames(hasImage ? 'text-white lg:mx-0' : 'text-primary')} />
                                </CardDescription>
                            </div>
                            {hasImage && link && <LinkArrow />}
                        </div>
                        {!hasImage && link && (
                            <LinkRenderer {...link}>
                                <Button
                                    className="mt-8"
                                    size="lg"
                                    onClick={() =>
                                        track(TRACKING_EVENT.PRESSED_BUTTON, {
                                            label: link?.label,
                                            url: href ? href : '',
                                        })
                                    }
                                >
                                    {link.label}
                                </Button>
                            </LinkRenderer>
                        )}
                    </div>
                )}
            </CardContent>
        ) : null;

    const El =
        hasImage && link ? (
            <LinkRenderer className="group absolute inset-0" {...link}>
                {Content}
            </LinkRenderer>
        ) : (
            Content
        );

    return (
        <Card
            className={classNames(hasImage && link ? 'transition duration-500 shadow-none hover:shadow-lg group' : '', cardRendererVariants({ span }))}
            style={{
                ...(order ? { order } : {}),
            }}
            video={video}
            image={typeof image === 'string' ? image : getImageUrl(image)}
            hoverImage={hoverImage}
            deferHoverImageLoad={deferHoverImageLoad}
            tintImage={tintImage !== false}
            sizes={
                sizes
                    ? sizes
                    : `(min-width: ${theme.screens['md']}) ${
                          imageRatio === CardImageAspectRatio['one-quarter']
                              ? '25vw'
                              : imageRatio === CardImageAspectRatio.half
                              ? '50vw'
                              : imageRatio === CardImageAspectRatio['two-thirds']
                              ? '66vw'
                              : imageRatio === CardImageAspectRatio['one-third']
                              ? '33vw'
                              : '100vw'
                      }, 100vw`
            }
            aspectRatio={imageRatio}
            background={hasImage ? 'none' : backgroundColour ? (backgroundColour as CardBackgroundColourVariant) : 'shell'}
        >
            {El}
        </Card>
    );
};

export default CardRenderer;
